import React, { useState, useEffect, useRef } from 'react';
import { Responsive as ResponsiveGridLayout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { backendService } from '../services/backendService';
import { authHeaders } from '../utils/auth';
import LexaOmniV2 from './LexaOmniV2';

const DEFAULT_LAYOUT = [
    { i: 'models',    x: 0,  y: 0,  w: 6,  h: 4  },
    { i: 'inference', x: 6,  y: 0,  w: 6,  h: 9  },
    { i: 'persona',   x: 0,  y: 4,  w: 6,  h: 5  },
    { i: 'lora',      x: 0,  y: 9,  w: 12, h: 4  },
    { i: 'lexa-chat', x: 0,  y: 13, w: 12, h: 11 },
];

export const OmniAdminPanel: React.FC = () => {
    const [config, setConfig] = useState({
        max_new_tokens: 2048,
        temperature: 0.5,
        repetition_penalty: 1.05,
        fps: 5,
        asr_enabled: true,
        tts_enabled: true,
        lora_active: null as string | null,
        max_slice_nums: 1,
        max_inp_length: 4352,
        top_p: 0.8,
        top_k: 100
    });

    const [adapters, setAdapters] = useState<string[]>([]);
    const [systemPrompt, setSystemPrompt] = useState("");
    const [personaName, setPersonaName] = useState("");
    const [availableModels, setAvailableModels] = useState<{ id: string, name?: string, status?: string }[]>([]);
    const [selectedModels, setSelectedModels] = useState({
        vision: localStorage.getItem('omni_model_vision') || 'qwen3-vl:4b',
        chat: localStorage.getItem('omni_model_chat') || 'qwen3-vl:4b',
        agent: localStorage.getItem('omni_model_agent') || 'steellord81/deepseek-r1:latest',
    });
    const [configSyncStatus, setConfigSyncStatus] = useState<'idle' | 'syncing' | 'ok' | 'error'>('idle');
    const [promptHistory, setPromptHistory] = useState<{ name: string, prompt: string }[]>(() => {
        try {
            return JSON.parse(localStorage.getItem('persona_history') || '[]');
        } catch {
            return [];
        }
    });

    // Draggable Layout State
    const [isLocked, setIsLocked] = useState(true);
    const [layouts, setLayouts] = useState<any>({ lg: DEFAULT_LAYOUT });
    const [mounted, setMounted] = useState(false);
    
    // Custom width provider logic
    const [width, setWidth] = useState(1200);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setMounted(true);
        const saved = localStorage.getItem('omnigate_admin_layout');
        if (saved) {
            try { const p = JSON.parse(saved);
                if (p) { Object.keys(p).forEach(k => { if (Array.isArray(p[k])) p[k].forEach((i:any) => delete i.static); }); }
                setLayouts(p); } catch { }
        }

        if (containerRef.current) {
            setWidth(containerRef.current.clientWidth);
        }

        const resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                if (entry.contentRect.width > 0) {
                    setWidth(entry.contentRect.width);
                }
            }
        });

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => resizeObserver.disconnect();
    }, []);

    const onLayoutChange = (layout: any, allLayouts: any) => {
        setLayouts(allLayouts);
        localStorage.setItem('omnigate_admin_layout', JSON.stringify(allLayouts));
    };

    useEffect(() => {
        fetchConfig();
        fetchAdapters();
        fetchModels();
    }, []);

    // Persist model selections
    useEffect(() => {
        localStorage.setItem('omni_model_vision', selectedModels.vision);
        localStorage.setItem('omni_model_chat', selectedModels.chat);
        localStorage.setItem('omni_model_agent', selectedModels.agent);
    }, [selectedModels]);

    const fetchModels = async () => {
        try {
            const res = await fetch('/v1/models/verified', { headers: authHeaders() });
            if (res.ok) {
                const data = await res.json();
                const models = (data.data || []).map((m: any) => ({
                    id: m.id || m.name,
                    name: m.id || m.name,
                    status: m.status || 'verified',
                    provider: m.owned_by || 'unknown',
                }));
                setAvailableModels(models);
            }
        } catch (e) {
            console.warn("Could not fetch verified models", e);
        }
    };

    const fetchConfig = async () => {
        try {
            // W wersji produkcyjnej powinno byc: await backendService.getConfig()
            const res = await fetch(`/v1/mcp/omni-bridge/config`, { headers: authHeaders() });
            if (res.ok) {
                const data = await res.json();
                setConfig(prev => ({ ...prev, ...data }));
            }
        } catch (e) {
            console.warn("Could not fetch omni config", e);
        }
    };

    const fetchAdapters = async () => {
        try {
            const res = await fetch(`/v1/lora/list`, { headers: authHeaders() });
            if (res.ok) {
                const data = await res.json();
                setAdapters(data.adapters || []);
            }
        } catch (e) {
            console.warn("Could not fetch adapters", e);
        }
    };

    const updateConfig = async (newConfig: typeof config) => {
        setConfig(newConfig);
        try {
            await fetch(`/v1/mcp/omni-bridge/config`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify(newConfig)
            });
        } catch (e) {
            console.error("Config update failed", e);
        }
    };

    const savePersona = () => {
        if (!systemPrompt.trim()) return;
        const name = personaName.trim() || `Persona ${promptHistory.length + 1}`;
        const newHistory = [{ name, prompt: systemPrompt }, ...promptHistory.filter(p => p.prompt !== systemPrompt)].slice(0, 10);
        setPromptHistory(newHistory);
        localStorage.setItem('persona_history', JSON.stringify(newHistory));
        setPersonaName("");
    };

    const loadAdapter = async (path: string) => {
        try {
            const res = await fetch(`/v1/lora/load`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ adapter_path: path })
            });
            if (res.ok) {
                setConfig({ ...config, lora_active: path });
            }
        } catch (e) {
            console.error("Adapter load failed", e);
        }
    };

    const unloadAdapter = async () => {
        try {
            const res = await fetch(`/v1/lora/unload`, { method: 'POST', headers: authHeaders() });
            if (res.ok) {
                setConfig({ ...config, lora_active: null });
            }
        } catch (e) { }
    };

    if (!mounted) return null;

    return (
        <div ref={containerRef} className="flex flex-col h-full bg-[#131722] overflow-hidden min-h-[600px] relative pb-10">
            <style>{`
                .react-grid-layout { position: relative; transition: height 200ms ease; }
                .react-grid-item { transition: all 200ms ease; transition-property: left, top; }
                .react-grid-item.cssTransforms { transition-property: transform; }
                .react-grid-item.resizing { z-index: 1; will-change: width, height; }
                .react-grid-item.react-draggable-dragging { transition: none; z-index: 3; will-change: transform; }
                .react-grid-item.react-grid-placeholder { background: rgba(59, 130, 246, 0.2); border: 2px dashed rgba(59, 130, 246, 0.5); opacity: 0.5; z-index: 2; transition: none; }
                .drag-handle { cursor: grab; }
                .drag-handle:active { cursor: grabbing; }
                .panel-wrapper { height: 100%; display: flex; flex-direction: column; background: #1e222d; border: 1px solid #2a2e39; border-radius: 0.5rem; overflow: hidden; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.5); }
                .panel-wrapper:hover { border-color: rgba(59, 130, 246, 0.3); }
            `}</style>

            <div className="absolute -top-10 right-4 z-50 flex gap-2">
                <button onClick={() => setIsLocked(!isLocked)} className={`text-[9px] px-2 py-1 rounded border transition-colors ${isLocked ? 'bg-red-900/40 text-red-400 border-red-800' : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white'}`}>
                    <i className={`fas ${isLocked ? 'fa-lock' : 'fa-unlock'} mr-1`}></i> {isLocked ? 'Locked' : 'Unlocked'}
                </button>
                <button onClick={() => { setLayouts({ lg: DEFAULT_LAYOUT }); localStorage.removeItem('omnigate_admin_layout'); }} className="text-[9px] bg-slate-800 text-slate-400 hover:text-white px-2 py-1 rounded border border-slate-700">
                    <i className="fas fa-sync-alt mr-1"></i> Reset Layout
                </button>
            </div>

            
                <div className="flex justify-between items-center sm:mb-4 mb-2 px-2 bg-[#131722]/80 border border-[#2a2e39] rounded py-2">
                    <div className="flex items-center gap-2">
                        <button onClick={() => setIsLocked(!isLocked)} className={`text-[10px] px-3 py-1.5 rounded transition-all font-bold ${isLocked ? 'bg-red-500/20 text-red-400 border border-red-500/50 shadow-[0_0_10px_rgba(239,68,68,0.2)]' : 'bg-green-500/20 text-green-400 border border-green-500/50'}`}>
                            <i className={`fas ${isLocked ? 'fa-lock' : 'fa-unlock'} mr-1`}></i> {isLocked ? 'Blokada Włączona' : 'Odblokowane (Przeciągaj)'}
                        </button>
                    </div>
                </div>
    <ResponsiveGridLayout
                className="layout flex-1"
                width={width}
                layouts={isLocked ? Object.fromEntries(Object.entries(layouts).map(([k, v]) => [k, v.map((i: any) => ({ ...i, static: true }))])) : layouts}
                breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                rowHeight={50}
                onLayoutChange={onLayoutChange}
                // @ts-ignore
                isDraggable={!isLocked}
                // @ts-ignore
                isResizable={!isLocked}
                // @ts-ignore
                draggableHandle=".drag-handle"
                margin={[12, 12]}
            >
                {/* MODELS PANEL */}
                <div key="models" className="panel-wrapper">
                    <div className={`p-3 border-b border-[#2a2e39] bg-[#131722]/60 flex items-center justify-between ${!isLocked ? 'drag-handle' : 'cursor-default'} flex-shrink-0`}>
                        <div className="flex items-center gap-2">
                            <i className="fas fa-microchip text-blue-500"></i>
                            <h2 className="text-[10px] font-black text-white uppercase tracking-widest">Active Models</h2>
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={fetchModels} className="text-[9px] text-blue-400 hover:text-blue-300" title="Refresh models">
                                <i className="fas fa-sync-alt"></i> {availableModels.length > 0 ? `${availableModels.length} loaded` : 'loading...'}
                            </button>
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#0a0c10]">
                        {(['vision', 'chat', 'agent'] as const).map(role => (
                            <div key={role} className="flex flex-col gap-1">
                                <label className="text-[10px] font-bold text-slate-500 uppercase flex items-center gap-2">
                                    {role === 'vision' ? '👁️ Vision' : role === 'chat' ? '💬 Chat' : '🤖 Agent'}
                                </label>
                                <div className="flex gap-1 w-full">
                                    <select
                                        value={availableModels.some(m => m.id === selectedModels[role]) ? selectedModels[role] : '__custom__'}
                                        onChange={e => {
                                            if (e.target.value !== '__custom__') {
                                                setSelectedModels(prev => ({ ...prev, [role]: e.target.value }));
                                            }
                                        }}
                                        className="flex-1 bg-[#131722] border border-[#2a2e39] text-slate-300 text-[10px] px-2 py-1.5 rounded focus:outline-none focus:border-blue-500/50"
                                    >
                                        {availableModels.map(m => (
                                            <option key={m.id} value={m.id}>
                                                {(m as any).provider === 'ollama-local' ? '🖥️' : '🌐'} {m.id}
                                            </option>
                                        ))}
                                        {!availableModels.some(m => m.id === selectedModels[role]) && (
                                            <option value="__custom__">Custom: {selectedModels[role]}</option>
                                        )}
                                    </select>
                                    <input
                                        type="text"
                                        value={selectedModels[role]}
                                        onChange={e => setSelectedModels(prev => ({ ...prev, [role]: e.target.value }))}
                                        className="w-1/3 min-w-[80px] bg-[#1e222d] border border-[#2a2e39] text-slate-400 text-[9px] px-1.5 py-1 rounded font-mono focus:outline-none focus:border-blue-500/50"
                                        title="Custom model ID"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* INFERENCE PANEL */}
                <div key="inference" className="panel-wrapper">
                    <div className={`p-3 border-b border-[#2a2e39] bg-[#131722]/60 flex items-center justify-between ${!isLocked ? 'drag-handle' : 'cursor-default'} flex-shrink-0`}>
                        <div className="flex items-center gap-2">
                            <i className="fas fa-sliders-h text-blue-500"></i>
                            <h2 className="text-[10px] font-black text-white uppercase tracking-widest">Inference Parameters</h2>
                        </div>
                        <i className="fas fa-grip-lines text-slate-600"></i>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#0a0c10] custom-scrollbar">
                        <div>
                            <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase mb-1">
                                <span>Temperature</span>
                                <span className="text-purple-400">{config.temperature.toFixed(2)}</span>
                            </div>
                            <input type="range" min="0" max="2" step="0.05" value={config.temperature} onChange={e => updateConfig({ ...config, temperature: parseFloat(e.target.value) })} className="w-full accent-purple-500 h-1 bg-[#2a2e39] rounded-lg appearance-none cursor-pointer" />
                        </div>

                        <div>
                            <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase mb-1">
                                <span>Max New Tokens</span>
                                <span className="text-purple-400">{config.max_new_tokens}</span>
                            </div>
                            <input type="range" min="256" max="8192" step="256" value={config.max_new_tokens} onChange={e => updateConfig({ ...config, max_new_tokens: parseInt(e.target.value) })} className="w-full accent-purple-500 h-1 bg-[#2a2e39] rounded-lg appearance-none cursor-pointer" />
                        </div>

                        <div>
                            <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase mb-1">
                                <span>Top P</span>
                                <span className="text-purple-400">{config.top_p.toFixed(2)}</span>
                            </div>
                            <input type="range" min="0" max="1" step="0.05" value={config.top_p} onChange={e => updateConfig({ ...config, top_p: parseFloat(e.target.value) })} className="w-full accent-purple-500 h-1 bg-[#2a2e39] rounded-lg appearance-none cursor-pointer" />
                        </div>

                        <div>
                            <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase mb-1">
                                <span>Top K</span>
                                <span className="text-purple-400">{config.top_k}</span>
                            </div>
                            <input type="range" min="0" max="200" step="1" value={config.top_k} onChange={e => updateConfig({ ...config, top_k: parseInt(e.target.value) })} className="w-full accent-purple-500 h-1 bg-[#2a2e39] rounded-lg appearance-none cursor-pointer" />
                        </div>

                        <div>
                            <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase mb-1">
                                <span>Repetition Penalty</span>
                                <span className="text-purple-400">{config.repetition_penalty.toFixed(2)}</span>
                            </div>
                            <input type="range" min="0" max="3" step="0.05" value={config.repetition_penalty} onChange={e => updateConfig({ ...config, repetition_penalty: parseFloat(e.target.value) })} className="w-full accent-purple-500 h-1 bg-[#2a2e39] rounded-lg appearance-none cursor-pointer" />
                        </div>

                        <div>
                            <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase mb-1">
                                <span>Vision MAX_SLICE_NUMS</span>
                                <span className="text-blue-400">{config.max_slice_nums}</span>
                            </div>
                            <input type="range" min="1" max="9" step="1" value={config.max_slice_nums} onChange={e => updateConfig({ ...config, max_slice_nums: parseInt(e.target.value) })} className="w-full accent-blue-500 h-1 bg-[#2a2e39] rounded-lg appearance-none cursor-pointer" />
                            <p className="text-[8px] text-slate-500 mt-1">HD / OOM Control (reduce to 1 if VRAM errors occur)</p>
                        </div>

                        <div>
                            <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase mb-1">
                                <span>Video MAX_INP_LENGTH</span>
                                <span className="text-blue-400">{config.max_inp_length}</span>
                            </div>
                            <input type="range" min="1024" max="8192" step="256" value={config.max_inp_length} onChange={e => updateConfig({ ...config, max_inp_length: parseInt(e.target.value) })} className="w-full accent-blue-500 h-1 bg-[#2a2e39] rounded-lg appearance-none cursor-pointer" />
                        </div>
                    </div>
                </div>

                {/* PERSONA & MEMORY PANEL */}
                <div key="persona" className="panel-wrapper">
                    <div className={`p-3 border-b border-[#2a2e39] bg-[#131722]/60 flex items-center justify-between ${!isLocked ? 'drag-handle' : 'cursor-default'} flex-shrink-0`}>
                        <div className="flex items-center gap-2">
                            <i className="fas fa-brain text-blue-500"></i>
                            <h2 className="text-[10px] font-black text-white uppercase tracking-widest">Persona & Memory</h2>
                        </div>
                        <i className="fas fa-grip-lines text-slate-600"></i>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#0a0c10] custom-scrollbar">
                        <input
                            type="text"
                            placeholder="Persona Name (e.g., Ruthless Assistant)"
                            value={personaName}
                            onChange={e => setPersonaName(e.target.value)}
                            className="w-full bg-[#131722] border border-[#2a2e39] text-slate-200 text-xs px-2 py-1.5 rounded focus:outline-none focus:border-purple-500/50"
                        />
                        <textarea
                            className="w-full h-24 bg-[#131722] border border-[#2a2e39] rounded p-2 text-xs text-slate-300 focus:outline-none focus:border-purple-500/50 resize-none"
                            placeholder="Enter severe system prompt to define behavior..."
                            value={systemPrompt}
                            onChange={e => setSystemPrompt(e.target.value)}
                        />
                        <button onClick={savePersona} className="w-full text-[10px] bg-purple-600/20 text-purple-400 border border-purple-500/30 py-1.5 rounded uppercase font-bold hover:bg-purple-600/40 transition-colors">
                            Save Persona to Memory
                        </button>

                        {promptHistory.length > 0 && (
                            <div className="mt-3">
                                <span className="text-[9px] font-bold text-slate-500 uppercase">Saved Roles:</span>
                                <div className="mt-1 flex flex-wrap gap-2 text-[10px]">
                                    {promptHistory.map((ph, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => { setSystemPrompt(ph.prompt); setPersonaName(ph.name); }}
                                            className="bg-[#2a2e39] text-slate-300 hover:bg-[#3b3f4a] px-2 py-1 rounded truncate max-w-[150px]"
                                            title={ph.prompt}
                                        >
                                            {ph.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* LORA ADAPTERS */}
                <div key="lora" className="panel-wrapper">
                    <div className={`p-3 border-b border-[#2a2e39] bg-[#131722]/60 flex items-center justify-between ${!isLocked ? 'drag-handle' : 'cursor-default'} flex-shrink-0`}>
                        <div className="flex items-center gap-2">
                            <i className="fas fa-layer-group text-blue-500"></i>
                            <h2 className="text-[10px] font-black text-white uppercase tracking-widest">LoRA Adapters Manager</h2>
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={fetchAdapters} className="text-[9px] text-blue-400 hover:text-blue-300"><i className="fas fa-sync-alt"></i></button>
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 bg-[#0a0c10] flex flex-col custom-scrollbar">
                        {adapters.length === 0 ? (
                            <p className="text-[10px] text-slate-500 text-center py-2">No adapters found in saves/ directory</p>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 flex-1 auto-rows-min">
                                {adapters.map((adapter, idx) => {
                                    const isActive = config.lora_active === adapter;
                                    return (
                                        <div key={idx} className={`flex items-center justify-between p-2 rounded text-xs h-10 ${isActive ? 'bg-green-500/10 border border-green-500/30' : 'bg-[#131722] border border-[#2a2e39]'}`}>
                                            <span className="text-slate-300 truncate pr-2 w-full" title={adapter}>{adapter.split('/').pop()}</span>
                                            {isActive ? (
                                                <button onClick={unloadAdapter} className="text-[9px] bg-red-500/20 text-red-500 px-3 py-1 rounded font-bold shrink-0">UNLOAD</button>
                                            ) : (
                                                <button onClick={() => loadAdapter(adapter)} className="text-[9px] bg-blue-500/20 text-blue-400 px-3 py-1 rounded font-bold shrink-0">LOAD</button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        <div className="mt-auto pt-3 border-t border-[#2a2e39]">
                            <button className="w-full text-[10px] bg-orange-500/20 text-orange-400 border border-orange-500/30 py-1.5 rounded uppercase font-bold hover:bg-orange-500/40 transition-colors">
                                <i className="fas fa-hammer mr-1"></i> Trigger SFT Training (Subprocess)
                            </button>
                        </div>
                    </div>
                </div>

            </ResponsiveGridLayout>

        {/* ── Lexa Omni Chat ─────────────────────────────────────────────────── */}
        {/* Rendered outside grid to avoid grid-layout overflow clipping */}
        <div className="mt-2 rounded-lg border border-[#2a2e39] bg-[#1e222d] shadow-xl"
             style={{ minHeight: '600px', height: '700px' }}>
          <LexaOmniV2 />
        </div>
        
        </div>
    );
};
